/**
 * Admin Hooks
 * React Query hooks for Super Admin dashboard operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import type { PackageEntitlementsResponse, PackageQuotaRow } from '@/types/admin';
import {
  AdminUserListFilters,
  AdminTeamListFilters,
  AdminAgencyListFilters,
  ImpersonationHistoryFilters,
  ChangePackageRequest,
  ExtendTrialRequest,
  GrantAddonRequest,
  GrantTokenRequest,
  ChangeStatusRequest,
  CreateAgencyRequest,
  ConvertTeamToAgencyRequest,
  StartImpersonationRequest,
  AdminAffiliateFilters,
  AdminPayoutFilters,
  UpdateAffiliateStatusRequest,
  ProcessPayoutRequest,
  AdminPackageFilters,
  CreatePackageRequest,
  UpdatePackageRequest,
  SetPackageFeatureRequest,
  FailedPostsFilters,
  UpcomingPostsFilters,
  PublishedPostsFilters,
  MissedPostsFilters,
  SystemNotificationFilters,
  CreateNotificationRequest,
  CreateUserRequest,
  AgencyStaffRole,
  TeamRole,
  AddAgencyStaffAdminRequest,
  ChangeAgencyStaffRoleRequest,
  ChangeAgencyStaffReviewerRequest,
  AttachClientTeamRequest,
  CreateAffiliateRequest,
  UpdateAffiliateRequest,
  UpdateAgencyRequest,
} from '@/types/admin';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const adminQueryKeys = {
  users: {
    all: ['admin', 'users'] as const,
    list: (filters: AdminUserListFilters) => ['admin', 'users', 'list', filters] as const,
    detail: (id: number) => ['admin', 'users', 'detail', id] as const,
    counts: ['admin', 'users', 'counts'] as const,
  },
  teams: {
    all: ['admin', 'teams'] as const,
    list: (filters: AdminTeamListFilters) => ['admin', 'teams', 'list', filters] as const,
    detail: (id: number) => ['admin', 'teams', 'detail', id] as const,
    counts: ['admin', 'teams', 'counts'] as const,
    usage: (id: number) => ['admin', 'teams', 'usage', id] as const,
  },
  agencies: {
    all: ['admin', 'agencies'] as const,
    list: (filters: AdminAgencyListFilters) => ['admin', 'agencies', filters] as const,
    detail: (id: number) => ['admin', 'agencies', id] as const,
  },
  impersonation: {
    active: ['admin', 'impersonation', 'active'] as const,
    history: (filters: ImpersonationHistoryFilters) => ['admin', 'impersonation', 'history', filters] as const,
  },
  system: {
    overview: ['admin', 'system', 'overview'] as const,
    aiUsage: ['admin', 'system', 'ai-usage'] as const,
    publishing: ['admin', 'system', 'publishing'] as const,
    failedPostStats: ['admin', 'system', 'failed-post-stats'] as const,
    mediaCleanup: ['admin', 'system', 'media-cleanup'] as const,
    publishedPosts: (filters: PublishedPostsFilters) => ['admin', 'published-posts', filters] as const,
    missedPosts: (filters: MissedPostsFilters) => ['admin', 'missed-posts', filters] as const,
  },
  affiliates: {
    all: ['admin', 'affiliates'] as const,
    list: (filters: AdminAffiliateFilters) => ['admin', 'affiliates', 'list', filters] as const,
    detail: (id: number) => ['admin', 'affiliates', 'detail', id] as const,
    dashboard: ['admin', 'affiliates', 'dashboard'] as const,
  },
  payouts: {
    all: ['admin', 'payouts'] as const,
    list: (filters: AdminPayoutFilters) => ['admin', 'payouts', 'list', filters] as const,
  },
  packages: {
    all: ['admin', 'packages'] as const,
    list: (filters: AdminPackageFilters) => ['admin', 'packages', 'list', filters] as const,
    detail: (id: number) => ['admin', 'packages', 'detail', id] as const,
    quotaRegistry: ['admin', 'packages', 'quota-registry'] as const,
    featureRegistry: ['admin', 'packages', 'feature-registry'] as const,
    entitlements: (id: number) => ['admin', 'packages', 'entitlements', id] as const,
    quotas: (id: number) => ['admin', 'packages', 'quotas', id] as const,
    coverage: ['admin', 'packages', 'coverage'] as const,
    gatingMode: ['admin', 'packages', 'gating-mode'] as const,
  },
  dashboard: {
    revenue: ['admin', 'dashboard', 'revenue'] as const,
    trials: ['admin', 'dashboard', 'trials'] as const,
    channelHealth: ['admin', 'dashboard', 'channel-health'] as const,
    churn: ['admin', 'dashboard', 'churn'] as const,
    maintenance: ['admin', 'maintenance'] as const,
  },
  systemNotifications: {
    all: ['admin', 'systemNotifications'] as const,
    list: (filters: SystemNotificationFilters) => ['admin', 'systemNotifications', 'list', filters] as const,
    detail: (id: number) => ['admin', 'systemNotifications', 'detail', id] as const,
  },
};

// ============================================================================
// USER HOOKS
// ============================================================================

/**
 * Fetch users list with filters
 */
export function useAdminUsers(filters: AdminUserListFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.users.list(filters),
    queryFn: () => adminService.getUsers(filters),
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch single user by ID
 */
export function useAdminUser(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.users.detail(id),
    queryFn: () => adminService.getUserById(id),
    enabled: id > 0,
  });
}

/**
 * Fetch user counts (total, active, super admins)
 */
export function useAdminUserCounts() {
  return useQuery({
    queryKey: adminQueryKeys.users.counts,
    queryFn: () => adminService.getUserCounts(),
  });
}

/**
 * Update user details
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof adminService.updateUser>[1] }) =>
      adminService.updateUser(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(id) });
    },
  });
}

/**
 * Delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => adminService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Reset user password (sends email)
 */
export function useResetUserPassword() {
  return useMutation({
    mutationFn: (id: number) => adminService.resetUserPassword(id),
  });
}

/**
 * Change user role
 */
export function useChangeUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, role }: { id: number; role: Parameters<typeof adminService.changeUserRole>[1] }) =>
      adminService.changeUserRole(id, role),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(id) });
    },
  });
}

/**
 * Create a new user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequest) => adminService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.counts });
    },
  });
}

/**
 * Add agency staff membership for a user
 */
export function useAddAgencyStaff(userId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { agencyId: number; role: AgencyStaffRole }) =>
      adminService.addAgencyStaff(userId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(userId) });
    },
  });
}

/**
 * Remove agency staff membership for a user
 */
export function useRemoveAgencyStaff(userId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agencyId: number) => adminService.removeAgencyStaff(userId, agencyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(userId) });
    },
  });
}

// ============================================================================
// TEAM HOOKS
// ============================================================================

/**
 * Fetch teams list with filters
 */
export function useAdminTeams(filters: AdminTeamListFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.teams.list(filters),
    queryFn: () => adminService.getTeams(filters),
    placeholderData: (prev) => prev,
  });
}

/**
 * Create a new team with owner user
 */
export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof adminService.createTeam>[0]) =>
      adminService.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
    },
  });
}

/**
 * Fetch single team by ID
 */
export function useAdminTeam(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.teams.detail(id),
    queryFn: () => adminService.getTeamById(id),
    enabled: id > 0,
  });
}

/**
 * Fetch team counts (total, active, expired, trials)
 */
export function useAdminTeamCounts() {
  return useQuery({
    queryKey: adminQueryKeys.teams.counts,
    queryFn: () => adminService.getTeamCounts(),
  });
}

/**
 * Fetch team usage statistics
 */
export function useTeamUsage(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.teams.usage(id),
    queryFn: () => adminService.getTeamUsage(id),
    enabled: id > 0,
  });
}

/**
 * Update team details
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof adminService.updateTeam>[1] }) =>
      adminService.updateTeam(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(id) });
    },
  });
}

/**
 * Delete a team
 */
export function useDeleteTeam() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => adminService.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
    },
  });
}

/**
 * Change team package/plan
 */
export function useChangeTeamPackage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ChangePackageRequest }) =>
      adminService.changeTeamPackage(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(id) });
    },
  });
}

/**
 * Extend team trial period
 */
export function useExtendTeamTrial() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ExtendTrialRequest }) =>
      adminService.extendTeamTrial(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(id) });
    },
  });
}

/**
 * Change team status (active, suspended, etc.)
 */
export function useChangeTeamStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ChangeStatusRequest }) =>
      adminService.changeTeamStatus(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(id) });
    },
  });
}

/**
 * Grant an addon to a team
 */
export function useGrantAddon() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: GrantAddonRequest }) =>
      adminService.grantAddon(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(id) });
    },
  });
}

/**
 * Revoke an addon from a team
 */
export function useRevokeAddon() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ teamId, addonId }: { teamId: number; addonId: number }) =>
      adminService.revokeAddon(teamId, addonId),
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(teamId) });
    },
  });
}

/**
 * Grant AI tokens to a team
 */
export function useGrantTokens() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: GrantTokenRequest }) =>
      adminService.grantTokens(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(id) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.usage(id) });
    },
  });
}

/**
 * Add a user as a member to a team
 */
export function useAddTeamMember(teamId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { userId: number; teamRole: TeamRole }) =>
      adminService.addTeamMember(teamId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(teamId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Remove a user from a team
 */
export function useRemoveTeamMember(teamId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => adminService.removeTeamMember(teamId, userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(teamId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(userId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Change a team member's role
 */
export function useChangeMemberRole(teamId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, teamRole }: { userId: number; teamRole: TeamRole }) =>
      adminService.changeMemberRole(teamId, userId, { teamRole }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(teamId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Transfer team ownership to another member
 */
export function useTransferTeamOwnership(teamId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { newOwnerUserId: number }) =>
      adminService.transferTeamOwnership(teamId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.detail(teamId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
      // Refresh new owner's detail. Previously demoted owners will refetch when next visited.
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.newOwnerUserId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

// ============================================================================
// AGENCY HOOKS
// ============================================================================

/**
 * Fetch agencies list with filters
 */
export function useAdminAgencies(filters: AdminAgencyListFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.agencies.list(filters),
    queryFn: () => adminService.getAgencies(filters),
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch single agency by ID
 */
export function useAdminAgency(id: number | undefined) {
  return useQuery({
    queryKey: adminQueryKeys.agencies.detail(id!),
    queryFn: () => adminService.getAgencyById(id!),
    enabled: !!id && id > 0,
  });
}

/**
 * Create a new agency
 */
export function useCreateAgency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgencyRequest) => adminService.createAgency(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.all });
    },
  });
}

/**
 * Update agency details
 */
export function useUpdateAgency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof adminService.updateAgency>[1] }) =>
      adminService.updateAgency(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(id) });
    },
  });
}

/**
 * Delete an agency
 */
export function useDeleteAgency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteAgency(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.all });
    },
  });
}

/**
 * Convert a team to an agency
 */
export function useConvertTeamToAgency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConvertTeamToAgencyRequest) =>
      adminService.convertTeamToAgency(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
    },
  });
}

/**
 * Update agency basic info (name, email, phone) — Phase 3
 */
export function useUpdateAgencyInfo(agencyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAgencyRequest) =>
      adminService.updateAgencyInfo(agencyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(agencyId) });
    },
  });
}

// ============================================================================
// AGENCY STAFF ADMIN HOOKS (Phase 3)
// ============================================================================

/**
 * Add a user as staff to an agency (admin route)
 */
export function useAddAgencyStaffAdmin(agencyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AddAgencyStaffAdminRequest) =>
      adminService.addAgencyStaffAdmin(agencyId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(agencyId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Remove a staff member from an agency (admin route)
 */
export function useRemoveAgencyStaffAdmin(agencyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) =>
      adminService.removeAgencyStaffAdmin(agencyId, userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(agencyId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(userId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Change a staff member's role in an agency (admin route)
 */
export function useChangeAgencyStaffRoleAdmin(agencyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: ChangeAgencyStaffRoleRequest }) =>
      adminService.changeAgencyStaffRoleAdmin(agencyId, userId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(agencyId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Grant / revoke the agency-wide reviewer flag of a staff member (admin route)
 */
export function useChangeAgencyStaffReviewerAdmin(agencyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: ChangeAgencyStaffReviewerRequest }) =>
      adminService.changeAgencyStaffReviewerAdmin(agencyId, userId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(agencyId) });
    },
  });
}

// ============================================================================
// AGENCY CLIENT TEAMS ADMIN HOOKS (Phase 3)
// ============================================================================

/**
 * Attach a standalone team to an agency as a client
 */
export function useAttachClientTeam(agencyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AttachClientTeamRequest) =>
      adminService.attachClientTeam(agencyId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(agencyId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
    },
  });
}

/**
 * Detach a client team from an agency
 */
export function useDetachClientTeam(agencyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: number) =>
      adminService.detachClientTeam(agencyId, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.agencies.detail(agencyId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.teams.all });
    },
  });
}

// ============================================================================
// AFFILIATE CRUD HOOKS (Phase 3)
// ============================================================================

/**
 * Create a new affiliate
 */
export function useCreateAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateAffiliateRequest) =>
      adminService.createAffiliate(body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.dashboard });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

/**
 * Update affiliate settings (minPayout, customCommissionRate)
 */
export function useUpdateAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateAffiliateRequest }) =>
      adminService.updateAffiliate(id, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.detail(id) });
    },
  });
}

/**
 * Delete an affiliate (fails with 409 if they have conversions/payouts)
 */
export function useDeleteAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteAffiliate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.dashboard });
      // userId is not available from affiliate id alone — users.all covers list views;
      // the affected user's detail page will refetch when next visited.
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
    },
  });
}

// ============================================================================
// IMPERSONATION HOOKS
// ============================================================================

/**
 * Get active impersonation session (polls every 60 seconds)
 * Only runs if enabled (should only be enabled for super admins)
 */
export function useActiveImpersonation(enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.impersonation.active,
    queryFn: () => adminService.getActiveImpersonation(),
    refetchInterval: enabled ? 60000 : false, // Poll every 60 seconds only if enabled
    enabled,
    retry: false, // Don't retry on failure (avoids spam for non-admins)
  });
}

/**
 * Start impersonating a user
 */
export function useStartImpersonation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StartImpersonationRequest) =>
      adminService.startImpersonation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.impersonation.active });
    },
  });
}

/**
 * End active impersonation session
 */
export function useEndImpersonation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => adminService.endImpersonation(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.impersonation.active });
    },
  });
}

/**
 * Fetch impersonation history with filters
 */
export function useImpersonationHistory(filters: ImpersonationHistoryFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.impersonation.history(filters),
    queryFn: () => adminService.getImpersonationHistory(filters),
  });
}

// ============================================================================
// SYSTEM HEALTH HOOKS
// ============================================================================

/**
 * Fetch system overview statistics
 */
export function useSystemOverview() {
  return useQuery({
    queryKey: adminQueryKeys.system.overview,
    queryFn: () => adminService.getSystemOverview(),
  });
}

/**
 * Fetch AI usage statistics
 */
export function useAIUsageStats() {
  return useQuery({
    queryKey: adminQueryKeys.system.aiUsage,
    queryFn: () => adminService.getAIUsageStats(),
  });
}

/**
 * Fetch publishing statistics by platform
 */
export function usePublishingStats() {
  return useQuery({
    queryKey: adminQueryKeys.system.publishing,
    queryFn: () => adminService.getPublishingStats(),
  });
}

/**
 * Fetch failed post stats from the new endpoint
 */
export function useFailedPostStats() {
  return useQuery({
    queryKey: ['admin', 'system', 'failed-post-stats'] as const,
    queryFn: () => adminService.getFailedPostStats(),
  });
}

/**
 * Fetch media cleanup statistics
 */
export function useMediaCleanupStats() {
  return useQuery({
    queryKey: adminQueryKeys.system.mediaCleanup,
    queryFn: () => adminService.getMediaCleanupStats(),
  });
}

/**
 * Fetch failed posts with pagination
 */
export function useFailedPosts(filters: FailedPostsFilters = {}) {
  return useQuery({
    queryKey: ['admin', 'failed-posts', filters],
    queryFn: () => adminService.getFailedPosts(filters),
  });
}

/**
 * Fetch upcoming scheduled posts with pagination
 */
export function useUpcomingPosts(filters: UpcomingPostsFilters = {}) {
  return useQuery({
    queryKey: ['admin', 'upcoming-posts', filters],
    queryFn: () => adminService.getUpcomingPosts(filters),
  });
}

/**
 * Fetch published posts with pagination
 */
export function usePublishedPosts(filters: PublishedPostsFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.system.publishedPosts(filters),
    queryFn: () => adminService.getPublishedPosts(filters),
  });
}

/**
 * Fetch missed scheduled posts with pagination
 */
export function useMissedPosts(filters: MissedPostsFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.system.missedPosts(filters),
    queryFn: () => adminService.getMissedPosts(filters),
  });
}

/**
 * Retry a failed publishing post
 */
export function useRetryFailedPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.retryFailedPost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'failed-posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'missed-posts'] });
    },
  });
}

// ============================================================================
// AFFILIATE HOOKS
// ============================================================================

/**
 * Fetch affiliate dashboard stats
 */
export function useAffiliateDashboard() {
  return useQuery({
    queryKey: adminQueryKeys.affiliates.dashboard,
    queryFn: () => adminService.getAffiliateDashboard(),
  });
}

/**
 * Fetch affiliates list with filters
 */
export function useAffiliates(filters: AdminAffiliateFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.affiliates.list(filters),
    queryFn: () => adminService.getAffiliates(filters),
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch single affiliate by ID
 */
export function useAffiliate(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.affiliates.detail(id),
    queryFn: () => adminService.getAffiliateById(id),
    enabled: id > 0,
  });
}

/**
 * Update affiliate status
 */
export function useUpdateAffiliateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAffiliateStatusRequest }) =>
      adminService.updateAffiliateStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.dashboard });
    },
  });
}

/**
 * Fetch payouts list with filters
 */
export function usePayouts(filters: AdminPayoutFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.payouts.list(filters),
    queryFn: () => adminService.getPayouts(filters),
  });
}

/**
 * Process payout (approve/reject/pay)
 */
export function useProcessPayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ProcessPayoutRequest }) =>
      adminService.processPayout(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.payouts.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.affiliates.dashboard });
    },
  });
}

// ============================================================================
// PACKAGE HOOKS
// ============================================================================

/**
 * Fetch packages list with filters
 */
export function useAdminPackages(filters: AdminPackageFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.packages.list(filters),
    queryFn: () => adminService.getPackages(filters),
  });
}

/**
 * Fetch single package by ID
 */
export function useAdminPackage(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.packages.detail(id),
    queryFn: () => adminService.getPackageById(id),
    enabled: id > 0,
  });
}

/**
 * Create a new package
 */
export function useCreatePackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePackageRequest) => adminService.createPackage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.all });
    },
  });
}

/**
 * Update package details
 */
export function useUpdatePackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePackageRequest }) =>
      adminService.updatePackage(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.detail(id) });
    },
  });
}

/**
 * Delete a package
 */
export function useDeletePackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.deletePackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.all });
    },
  });
}

/**
 * Clone a package
 */
export function useClonePackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.clonePackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.all });
    },
  });
}

/**
 * Retry/trigger WC product provisioning for a package (WC-sync FAILED or
 * NONE + price>0). Returns the updated package with its new wcSyncStatus.
 */
export function usePublishPackageToShop() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.publishPackageToShop(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.all });
    },
  });
}

/**
 * Create/update an elastic package feature row (e.g. `wordpress`). Used for
 * feature toggles that live in the generic PackageFeature registry rather
 * than a dedicated Package column (canvaEnabled, brandCheckEnabled, etc.).
 */
export function useSetPackageFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packageId, data }: { packageId: number; data: SetPackageFeatureRequest }) =>
      adminService.setPackageFeature(packageId, data),
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.packages.detail(packageId) });
    },
  });
}

/** Feature registry without a package (create-mode draft). */
export function useFeatureRegistry(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.packages.featureRegistry,
    queryFn: () => adminService.getFeatureRegistry(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Quota definitions without a package (create-mode draft). */
export function useQuotaRegistry(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.packages.quotaRegistry,
    queryFn: () => adminService.getQuotaRegistry(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function usePackageEntitlements(packageId: number) {
  return useQuery({
    queryKey: adminQueryKeys.packages.entitlements(packageId),
    queryFn: () => adminService.getPackageEntitlements(packageId),
    enabled: packageId > 0,
  });
}

export function usePackageQuotas(packageId: number) {
  return useQuery({
    queryKey: adminQueryKeys.packages.quotas(packageId),
    queryFn: () => adminService.getPackageQuotas(packageId),
    enabled: packageId > 0,
  });
}

/** Set one monthly quota (null = unlimited). Saves on demand; the quota list refetches on settle. */
export function useSetPackageQuota(packageId: number) {
  const queryClient = useQueryClient();
  const queryKey = adminQueryKeys.packages.quotas(packageId);

  return useMutation({
    mutationFn: ({ quotaKey, monthlyLimit }: { quotaKey: string; monthlyLimit: number | null }) =>
      adminService.setPackageQuota(packageId, quotaKey, monthlyLimit),
    onSuccess: (_data, { quotaKey, monthlyLimit }) => {
      queryClient.setQueryData<PackageQuotaRow[]>(queryKey, (old) =>
        old?.map((q) => (q.quotaKey === quotaKey ? { ...q, monthlyLimit } : q))
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function usePackageFeatureCoverage() {
  return useQuery({
    queryKey: adminQueryKeys.packages.coverage,
    queryFn: () => adminService.getPackageFeatureCoverage(),
  });
}

/** Gating mode is informational; never retry on failure. */
export function useGatingMode() {
  return useQuery({
    queryKey: adminQueryKeys.packages.gatingMode,
    queryFn: () => adminService.getGatingMode(),
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Toggle one entitlement with optimistic update + rollback. Column-source keys
 * (canva, brand_check, gdrive, analytics, whitelabel) change the Package row,
 * so the package list/detail queries are refetched on settle.
 */
export function useSetPackageEntitlement(packageId: number) {
  const queryClient = useQueryClient();
  const queryKey = adminQueryKeys.packages.entitlements(packageId);

  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      adminService.setPackageEntitlement(packageId, key, enabled),
    onMutate: async ({ key, enabled }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient
        .getQueryData<PackageEntitlementsResponse>(queryKey)
        ?.features.find((f) => f.key === key);
      queryClient.setQueryData<PackageEntitlementsResponse>(queryKey, (old) =>
        old
          ? {
              ...old,
              features: old.features.map((f) =>
                f.key === key ? { ...f, enabled, hasRow: f.source === 'row' ? true : f.hasRow } : f
              ),
            }
          : old
      );
      return { previous };
    },
    // Roll back only the failed key, never the whole snapshot.
    onError: (_err, { key }, ctx) => {
      const prev = ctx?.previous;
      if (!prev) return;
      queryClient.setQueryData<PackageEntitlementsResponse>(queryKey, (old) =>
        old ? { ...old, features: old.features.map((f) => (f.key === key ? prev : f)) } : old
      );
    },
    onSettled: () => {
      // One prefix invalidation covers entitlements, coverage, list and detail;
      // the informational gating-mode query is excluded so it is not refetched.
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.packages.all,
        predicate: (q) => q.queryKey[2] !== 'gating-mode',
      });
    },
  });
}

// ============================================================================
// DASHBOARD ANALYTICS HOOKS
// ============================================================================

/**
 * Fetch revenue statistics (MRR, ARR, paying teams)
 */
export function useRevenueStats() {
  return useQuery({
    queryKey: adminQueryKeys.dashboard.revenue,
    queryFn: () => adminService.getRevenueStats(),
  });
}

/**
 * Fetch trial statistics (active, expiring, conversions)
 */
export function useTrialStats() {
  return useQuery({
    queryKey: adminQueryKeys.dashboard.trials,
    queryFn: () => adminService.getTrialStats(),
  });
}

/**
 * Fetch channel health statistics (expired tokens, reconnections needed)
 */
export function useChannelHealthStats() {
  return useQuery({
    queryKey: adminQueryKeys.dashboard.channelHealth,
    queryFn: () => adminService.getChannelHealthStats(),
  });
}

/**
 * Fetch churn statistics (cancellations, churn rate)
 */
export function useChurnStats() {
  return useQuery({
    queryKey: adminQueryKeys.dashboard.churn,
    queryFn: () => adminService.getChurnStats(),
  });
}

/**
 * Fetch maintenance mode status
 */
export function useMaintenanceStatus() {
  return useQuery({
    queryKey: adminQueryKeys.dashboard.maintenance,
    queryFn: () => adminService.getMaintenanceStatus(),
  });
}

/**
 * Set maintenance mode (enable/disable with message)
 */
export function useSetMaintenanceMode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { enabled: boolean; message?: string }) =>
      adminService.setMaintenanceMode(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboard.maintenance });
    },
  });
}

// ============================================================================
// SYSTEM NOTIFICATIONS HOOKS
// ============================================================================

/**
 * Fetch system notifications list with filters
 */
export function useSystemNotifications(filters: SystemNotificationFilters = {}) {
  return useQuery({
    queryKey: adminQueryKeys.systemNotifications.list(filters),
    queryFn: () => adminService.getSystemNotifications(filters),
  });
}

/**
 * Fetch single system notification by ID
 */
export function useSystemNotification(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.systemNotifications.detail(id),
    queryFn: () => adminService.getSystemNotification(id),
    enabled: id > 0,
  });
}

/**
 * Create a new system notification
 */
export function useCreateSystemNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNotificationRequest) =>
      adminService.createSystemNotification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.systemNotifications.all });
    },
  });
}

/**
 * Update system notification
 */
export function useUpdateSystemNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateNotificationRequest> }) =>
      adminService.updateSystemNotification(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.systemNotifications.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.systemNotifications.detail(id) });
    },
  });
}

/**
 * Delete a system notification
 */
export function useDeleteSystemNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteSystemNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.systemNotifications.all });
    },
  });
}

/**
 * Publish a system notification (set isActive = true)
 */
export function usePublishSystemNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.publishSystemNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.systemNotifications.all });
    },
  });
}

/**
 * Unpublish a system notification (set isActive = false)
 */
export function useUnpublishSystemNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.unpublishSystemNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.systemNotifications.all });
    },
  });
}

// ============================================================================
// AUDIT LOG HOOKS
// ============================================================================

/**
 * Fetch audit logs for a specific entity (team, agency, user)
 */
export function useEntityAuditLogs(
  entityType: string,
  entityId: number,
  page: number = 1,
  limit: number = 20
) {
  return useQuery({
    queryKey: ['admin', 'audit-logs', entityType, entityId, page, limit],
    queryFn: () => adminService.getEntityAuditLogs(entityType, entityId, page, limit),
    enabled: entityId > 0 && !!entityType,
  });
}
